export const ReservationLogChangeListItem = Symbol("ReservationLogChangeListItem");
export interface ReservationLogChangeListItem {
    [ReservationLogChangeListItem]: boolean;
    FieldName: string;
}

export const ReservationLogScalarChangeListItem = Symbol("ReservationLogScalarChangeListItem");
export interface ReservationLogScalarChangeListItem extends ReservationLogChangeListItem {
    [ReservationLogScalarChangeListItem]: boolean;
    OldValue: string | null;
    NewValue: string | null;
}

export function isReservationLogScalarChangeListItem(val: ReservationLogChangeListItem): val is ReservationLogScalarChangeListItem {
    return val && (val as ReservationLogScalarChangeListItem)[ReservationLogScalarChangeListItem] === true;
}

export const ReservationLogCompositeChangeListItem = Symbol("ReservationLogCompositeChangeListItem");
export interface ReservationLogCompositeChangeListItem extends ReservationLogChangeListItem {
    [ReservationLogCompositeChangeListItem]: boolean;
    Changes: ReservationLogChangeListItem[];
}

export function isReservationLogCompositeChangeListItem(val: ReservationLogChangeListItem): val is ReservationLogCompositeChangeListItem {
    return val && (val as ReservationLogCompositeChangeListItem)[ReservationLogCompositeChangeListItem] === true;
}

export interface ReservationLogChangeListItemDto {
    $type: string;
    fieldName: string;
}

export interface ReservationLogScalarChangeListItemDto extends ReservationLogChangeListItemDto {
    oldValue: string | null;
    newValue: string | null;
}

export interface ReservationLogCompositeChangeListItemDto extends ReservationLogChangeListItemDto {
    // Composite can contain both scalar and composite items
    changes: ReservationLogChangeListItemDto[];
}

/**
 * Extensible (de)serialization registry for log change list items.
 * Consumers can register custom handlers keyed by DTO $type or a logical kind.
 */
export type ReservationLogChangeListItemDeserializer = (dto: ReservationLogChangeListItemDto) => ReservationLogChangeListItem;
export type ReservationLogChangeListItemSerializer = (item: ReservationLogChangeListItem) => ReservationLogChangeListItemDto;

const changeItemDeserializers = new Map<string, ReservationLogChangeListItemDeserializer>();
const changeItemSerializers = new Map<string, ReservationLogChangeListItemSerializer>();

export function registerReservationLogChangeListItemDeserializer(kind: string, fn: ReservationLogChangeListItemDeserializer): void {
    changeItemDeserializers.set(kind, fn);
}
export function registerReservationLogChangeListItemSerializer(kind: string, fn: ReservationLogChangeListItemSerializer): void {
    changeItemSerializers.set(kind, fn);
}

export function deserializeReservationLogChangeListItem(dto: ReservationLogChangeListItemDto): ReservationLogChangeListItem {
    if (!dto) {
        return null;
    }

    // Custom handlers first
    const custom = changeItemDeserializers.get(dto.$type);
    if (custom) {
        return custom(dto);
    }

    // Built-in fallbacks
    switch (dto.$type) {
        case "Scalar": {
            const scalarDto = dto as ReservationLogScalarChangeListItemDto;

            const scalarItem = createReservationLogScalarChangeListItem();
            scalarItem.FieldName = scalarDto.fieldName;
            scalarItem.OldValue = scalarDto.oldValue;
            scalarItem.NewValue = scalarDto.newValue;

            return scalarItem;
        }
        case "Composite": {
            const compositeDto = dto as ReservationLogCompositeChangeListItemDto;
            const compositeEntry = createReservationLogCompositeChangeListItem();
            compositeEntry.FieldName = compositeDto.fieldName;
            compositeEntry.Changes = (compositeDto.changes ?? []).map(deserializeReservationLogChangeListItem);
            return compositeEntry;
        }
        default:
            throw new Error(`Invalid logEntry type: ${dto.$type}`);
    }
}

export function serializeReservationLogChangeListItem(item: ReservationLogChangeListItem): ReservationLogChangeListItemDto {
    // Built-in kinds
    if (isReservationLogScalarChangeListItem(item)) {
        const dto: ReservationLogScalarChangeListItemDto = {
            $type: "Scalar",
            fieldName: item.FieldName,
            oldValue: item.OldValue,
            newValue: item.NewValue
        };
        return dto;
    }
    if (isReservationLogCompositeChangeListItem(item)) {
        const dto: ReservationLogCompositeChangeListItemDto = {
            $type: "Composite",
            fieldName: item.FieldName,
            changes: item.Changes.map(serializeReservationLogChangeListItem) as ReservationLogChangeListItemDto[]
        };
        return dto;
    }

    // Custom kinds by $kind
    const anyItem: any = item as any;
    const kind: string | undefined = typeof anyItem?.$kind === "string" ? anyItem.$kind : undefined;
    if (kind) {
        const s = changeItemSerializers.get(kind);
        if (s) {
            return s(item);
        }
    }

    throw new Error("Cannot serialize ReservationLogChangeListItem - unknown kind");
}

export function createReservationLogScalarChangeListItem(): ReservationLogScalarChangeListItem {
    const logEntry: ReservationLogScalarChangeListItem = {
        [ReservationLogChangeListItem]: true,
        [ReservationLogScalarChangeListItem]: true,
        FieldName: "",
        OldValue: null,
        NewValue: null
    };

    return logEntry;
};

export function createReservationLogCompositeChangeListItem(): ReservationLogCompositeChangeListItem {
    const logEntry: ReservationLogCompositeChangeListItem = {
        [ReservationLogChangeListItem]: true,
        [ReservationLogCompositeChangeListItem]: true,
        FieldName: "",
        Changes: []
    };

    return logEntry;
}

/**
 * Register built-in handlers at module load so consumers can override or extend.
 */
registerReservationLogChangeListItemDeserializer("Scalar", (dto) => {
    const d = dto as ReservationLogScalarChangeListItemDto;
    const item = createReservationLogScalarChangeListItem();
    item.FieldName = d.fieldName;
    item.OldValue = d.oldValue;
    item.NewValue = d.newValue;
    return item;
});

registerReservationLogChangeListItemDeserializer("Composite", (dto) => {
    const d = dto as ReservationLogCompositeChangeListItemDto;
    const item = createReservationLogCompositeChangeListItem();
    item.FieldName = d.fieldName;
    item.Changes = (d.changes ?? []).map(deserializeReservationLogChangeListItem);
    return item;
});

registerReservationLogChangeListItemSerializer("Scalar", (item) => {
    if (!isReservationLogScalarChangeListItem(item)) {
        throw new Error("Serializer 'Scalar' received non-scalar item");
    }
    const dto: ReservationLogScalarChangeListItemDto = {
        $type: "Scalar",
        fieldName: item.FieldName,
        oldValue: item.OldValue,
        newValue: item.NewValue
    };
    return dto;
});

registerReservationLogChangeListItemSerializer("Composite", (item) => {
    if (!isReservationLogCompositeChangeListItem(item)) {
        throw new Error("Serializer 'Composite' received non-composite item");
    }
    const dto: ReservationLogCompositeChangeListItemDto = {
        $type: "Composite",
        fieldName: item.FieldName,
        changes: item.Changes.map(serializeReservationLogChangeListItem) as ReservationLogChangeListItemDto[]
    };
    return dto;
});
